import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery, useTheme, Box, Grid } from "@mui/material";
import { useAlerts } from "../../hooks/useAlerts";
import { RuleInstance } from "../../types/models/RuleInstance";
import { ContentRevision } from "../../types/models/ContentRevision";
import { AlertViewMode } from "../../constants/AppEnums";
import { AppState } from "../../redux/store";
import { RuleUnitState } from "../../types/models/RuleUnitState";
import {
  getContentList,
  getMyProgress,
  getMyProgressDetail,
  selectRule,
} from "../../redux/actions";
import AppInfoView from "../../components/AppBase/AppInfoView";
import AppScrollbar from "../../components/AppBase/AppScrollbar";
import LoadingError from "../../components/LoadingError";
import RulesHeader from "../../components/RulesHeader";
import RuleInstanceDetail from "../rules/RuleInstanceDetail";
import EmptyResult from "../rules/EmptyResult";
import AlertsCard from "./AlertsCard";
import { RuleInstanceTargetItem } from "../rules/ContentRevisionView/RuleInstanceList";
import { getMatchingAlertRuleInstances } from "../../redux/reducers/RuleUnitReducer";
import { getRuleInstancesForSearchText } from "../../utils/RuleUtil";

export default function AlertsPage() {
  const { invalidAccess } = useAlerts();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isSmallScreen = useMediaQuery(`(width <= ${theme.breakpoints.values.md}px)`);
  const ruleUnitState = useSelector<AppState, RuleUnitState>((state) => state.ruleUnit);

  const [selectedRule, setSelectedRule] = useState<{
    Rule: RuleInstance;
    Content: ContentRevision;
  }>();
  const [showRuleModal, setShowRuleModal] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<string>(AlertViewMode.Alert);
  const [displayTargetItem, setDisplayTargetItem] = useState<RuleInstanceTargetItem>();
  const [baseAlerts, setBaseAlerts] = useState<{ alerts: RuleInstance[]; history: RuleInstance[] }>(
    {
      alerts: [],
      history: [],
    }
  );
  const [filteredAlerts, setFilteredAlerts] = useState<{
    alerts: RuleInstance[];
    history: RuleInstance[];
  }>({
    alerts: [],
    history: [],
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const contentHeight = "calc(100vh - 100px)";
  const { contentId } = useParams();

  const applySearchFilter = (
    alerts: { alerts: RuleInstance[]; history: RuleInstance[] },
    searchText?: string
  ) => {
    if (!searchText || searchText.length <= 1) {
      setFilteredAlerts(alerts);
      return;
    }

    // Apply search filtering
    const filteredActiveAlerts = getRuleInstancesForSearchText(alerts.alerts, searchText) || [];
    const filteredHistoricalAlerts =
      getRuleInstancesForSearchText(alerts.history, searchText) || [];

    setFilteredAlerts({
      alerts: filteredActiveAlerts,
      history: filteredHistoricalAlerts,
    });
  };

  const refreshAlerts = () => {
    setIsLoading(true);
    dispatch(
      getContentList(() => {
        dispatch(
          getMyProgressDetail(() => {
            dispatch(
              getMyProgress(() => {
                setIsLoading(false);
                setViewMode("alert");
                setSelectedRule(undefined);
                navigate("/alerts");
              })
            );
          })
        );
      })
    );
  };

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      getContentList(() => {
        dispatch(
          getMyProgressDetail(() => {
            dispatch(
              getMyProgress(() => {
                setIsLoading(false);
              })
            );
          })
        );
      })
    );
  }, []);

  useEffect(() => {
    const activeAlerts = ruleUnitState?.myAlerts?.filter((item) => !item.AcknowledgeAt);

    const historicalAlerts = ruleUnitState?.myAlerts?.filter((item) => item.AcknowledgeAt);

    const updatedActiveAlerts = getMatchingAlertRuleInstances(
      ruleUnitState.baseAlertContentRevisionDetails,
      activeAlerts
    );

    const updatedHistoricalAlerts = getMatchingAlertRuleInstances(
      ruleUnitState.baseAlertContentRevisionDetails,
      historicalAlerts
    );

    const newBaseAlerts = {
      alerts: updatedActiveAlerts.sort((a, b) => {
        const dateA = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
        const dateB = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
        return dateA - dateB;
      }),
      history: updatedHistoricalAlerts.sort((a, b) => {
        const dateA = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
        const dateB = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
        return dateB - dateA;
      }),
    };
    setBaseAlerts(newBaseAlerts);
  }, [ruleUnitState.myAlerts, ruleUnitState.baseAlertContentRevisionDetails]);

  // Search alerts when search text changes
  useEffect(() => {
    applySearchFilter(baseAlerts, ruleUnitState.searchText);
  }, [ruleUnitState.searchText, baseAlerts]);

  useEffect(() => {
    if (!contentId) return;

    const contentRevision = ruleUnitState.baseAlertContentRevisionDetails?.find(
      (detail) => detail.ContentRevisionId === contentId
    );

    if (contentRevision) {
      const rule = contentRevision?.RuleInstances ? contentRevision?.RuleInstances[0] : undefined;
      if (rule) {
        if (baseAlerts.history.find((alert) => alert.RuleId === rule.RuleId)) {
          setViewMode(AlertViewMode.History);
        }

        if (!selectedRule) {
          setSelectedRule({ Rule: rule, Content: contentRevision });
          dispatch(selectRule(rule));
          setShowRuleModal(true);
          setDisplayTargetItem({
            contentId: contentId,
            id: rule.RuleId,
            name: rule.DisplayName,
            type: "rule",
          });
        }

        if (selectedRule?.Rule.RuleInstanceId !== rule.RuleInstanceId) {
          setSelectedRule({ Rule: rule, Content: contentRevision });
          dispatch(selectRule(rule));

          setShowRuleModal(true);
          setDisplayTargetItem({
            contentId: contentId,
            id: rule.RuleId,
            name: rule.DisplayName,
            type: "rule",
          });
        }
      }
    }
  }, [contentId, ruleUnitState.myAlerts, ruleUnitState.baseAlertContentRevisionDetails]);

  useEffect(() => {
    if (selectedRule) {
      if (contentId) return;
      // Clear selection if the searched rules does not include selected rule
      if (
        viewMode === AlertViewMode.History &&
        filteredAlerts.history &&
        !filteredAlerts.history.find((rule) => rule.RuleId === selectedRule.Rule.RuleId)
      ) {
        setSelectedRule(undefined);
        dispatch(selectRule(undefined));
      } else if (
        viewMode === AlertViewMode.Alert &&
        filteredAlerts.alerts &&
        !filteredAlerts.alerts.find((rule) => rule.RuleId === selectedRule.Rule.RuleId)
      ) {
        setSelectedRule(undefined);
        dispatch(selectRule(undefined));
      }
    }
  }, [dispatch, filteredAlerts, selectedRule]);

  const getTitle = () => {
    const env = process.env?.REACT_APP_ENVIRONMENT;
    if (env === "LOCAL" || env === "DEV" || env === "UAT") {
      return `Alerts (${env})`;
    } else {
      return "Alerts";
    }
  };

  return invalidAccess ? (
    <LoadingError />
  ) : (
    <>
      <Box
        display="grid"
        gridTemplateRows="min-content 1fr"
        minHeight="100vh"
        sx={{ backgroundColor: theme.page.bgColor }}
      >
        <RulesHeader title={getTitle()} isRefreshing={isLoading} refreshPage={refreshAlerts} />
        <Grid container sx={{ height: "100%" }}>
          <Grid item xs={12} md={6} lg={5} sx={{ pt: 2, px: 1 }}>
            <AppScrollbar sx={{ height: contentHeight }}>
              <Box sx={{ px: 1 }}>
                <AlertsCard
                  handleSelectAlert={(rule, content) => {
                    navigate(`/alerts/${content.ContentRevisionId}`);
                  }}
                  handleSelectViewMode={(viewMode) => {
                    setViewMode(viewMode);
                    // Clear detail
                    setSelectedRule(undefined);
                  }}
                  targetItem={displayTargetItem}
                  viewMode={viewMode}
                  alerts={filteredAlerts}
                />
              </Box>
            </AppScrollbar>
          </Grid>

          {/* Rule Content Area */}
          <Grid item xs={12} md={6} lg={7}>
            {isSmallScreen ? (
              // Modal version
              <RuleInstanceDetail
                isModal={true}
                key={selectedRule?.Rule.RuleInstanceId}
                displayFeedbackButtons={viewMode === AlertViewMode.Alert}
                contentRevision={selectedRule?.Content}
                rule={selectedRule?.Rule}
                isShowDetail={showRuleModal}
                setIsShowDetail={setShowRuleModal}
                displayFavouritePinButton={false}
              />
            ) : (
              <Box py={2} pr={2}>
                {selectedRule ? (
                  <AppScrollbar sx={{ height: contentHeight }}>
                    <Box
                      sx={{ px: 2, py: 1 }}
                      minHeight={contentHeight}
                      border={1}
                      bgcolor="white"
                      borderColor="grey.200"
                      borderRadius={1}
                      flexGrow={1}
                    >
                      <RuleInstanceDetail
                        key={selectedRule?.Rule.RuleInstanceId}
                        displayFeedbackButtons={viewMode === AlertViewMode.Alert}
                        isModal={false}
                        contentRevision={selectedRule?.Content}
                        rule={selectedRule?.Rule}
                        displayFavouritePinButton={false}
                        handleAcknowledge={() => setSelectedRule(undefined)}
                      />
                    </Box>
                  </AppScrollbar>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid",
                      borderColor: "grey.200",
                      backgroundColor: "white",
                      height: contentHeight,
                    }}
                  >
                    <EmptyResult>Click on an item to see more details.</EmptyResult>
                  </Box>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      <AppInfoView />
    </>
  );
}
